import React, { useEffect, useState, useContext, useCallback } from 'react';
import ViewScenarioTitlePage from './ViewScenarioTitlePage';
import ViewSituation from './ViewSituation';
import { useParams, useMatch, useNavigate, useLocation } from 'react-router-dom';
import { useScenario } from '../../../context/ScenarioContext';
import axios from 'axios';
import ViewTableOfContents from './ViewTableOfContents';
import styles from './styles/ViewScenario.module.css';
import AuthContext from '../../../context/AuthContext';
import ScenarioPassword from './ScenarioPassword';
import { FaListOl, FaRedo, FaTimes } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import Loading from '../../loading/Loading.js';

const ViewScenario = () => {
    const location = useLocation();
    const { userId: authUserId } = useContext(AuthContext);
    const { scenarioId } = useParams();
    const [scenario, setScenario] = useState(null);
    const [canvas, setCanvas] = useState(null);
    const { currentSituations, setCurrentSituationId: contextSetCurrentSituationId, setLogId } = useScenario();
    
    const [hasAccess, setHasAccess] = useState(location.state?.hasAccess ?? false);
    const [fromFeedback, setFromFeedback] = useState(location.state?.fromFeedback ?? false);
    const [passwordError, setPasswordError] = useState(false);
    const [isToCVisible, setIsToCVisible] = useState(false);
    const preview = useMatch("/create/:scenarioId/preview");
    const navigate = useNavigate();

    const [fetchedUser, setFetchedUser] = useState(null);
    const [userLoading, setUserLoading] = useState(true);
    const [isOverallLoading, setIsOverallLoading] = useState(true);
    const [isUserDataLoaded, setIsUserDataLoaded] = useState(false);
    const [isScenarioDataLoaded, setIsScenarioDataLoaded] = useState(false);

    const [userKeys, setUserKeys] = useState(() => {
        if (authUserId) {
            return [];
        } else {
            const storedKeyObjectsRaw = sessionStorage.getItem('userKeys');
            const storedKeyObjects = storedKeyObjectsRaw ? JSON.parse(storedKeyObjectsRaw) : [];
            return storedKeyObjects.map(ko => ko.keyId);
        }
    });

    const currentSituationId = currentSituations[scenarioId];

    
    useEffect(() => {
        if (location.state?.currentSituationId && scenarioId) {
            contextSetCurrentSituationId(scenarioId, location.state.currentSituationId);
        }
    }, [location.state?.currentSituationId, scenarioId, contextSetCurrentSituationId]);
    
    
    useEffect(() => {
        setIsUserDataLoaded(false);
        setIsOverallLoading(true);
        setUserLoading(true);
    
        const fetchUserData = async () => {
            if (!authUserId) {
                const storedKeyObjectsRaw = sessionStorage.getItem('userKeys');
                const storedKeyObjects = storedKeyObjectsRaw ? JSON.parse(storedKeyObjectsRaw) : [];
                setUserKeys(storedKeyObjects.map(ko => ko.keyId));
                setFetchedUser(null);
                setIsUserDataLoaded(true);
                setUserLoading(false);
                return;
            }
    
            try {
                const userResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/users/${authUserId}?populateAssignments=true`,
                    { withCredentials: true }
                );
                const userData = userResponse.data;
                setFetchedUser(userData);
                setUserKeys(userData.keys || []);
    
                if (userData?.incompleteLogs) {
                    for (const [logId, log] of Object.entries(userData.incompleteLogs)) {
                        if (log.scenarioId === scenarioId) {
                            setLogId(logId);
                            break;
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                setFetchedUser(null);
                setUserKeys([]);
            } finally {
                setIsUserDataLoaded(true);
                setUserLoading(false);
            }
        };
    
        fetchUserData();
    }, [authUserId, scenarioId, setLogId]);

    useEffect(() => {
        if (!authUserId) {
        }
    }, [authUserId, userKeys]);
        
    useEffect(() => {
        setIsScenarioDataLoaded(false);
        setIsOverallLoading(true);
    
        const fetchScenarioData = async () => {
            if (!scenarioId) return;
            try {
                const [scenarioResponse, canvasResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/${scenarioId}`),
                    axios.get(`${process.env.REACT_APP_API_URL_LOCAL}/api/canvas/${scenarioId}`),
                ]);
    
                setScenario(scenarioResponse.data);
                setCanvas(canvasResponse.data);
                setPasswordError(false);
            } catch (error) {
                console.error(`Error fetching scenario data: ${error}`);
                setScenario(null);
                setCanvas(null);
            } finally {
                setIsScenarioDataLoaded(true);
            }
        };
    
        fetchScenarioData();
    }, [scenarioId]);

    
    useEffect(() => {
        if (isUserDataLoaded && isScenarioDataLoaded) {
            let finalAccess = false;
            if (preview) {
                finalAccess = true;
            } else if (scenario && !scenario.isPrivate) {
                finalAccess = true;
            } else if (authUserId && fetchedUser?.activeAssignments?.some(a => a?.scenario?._id === scenarioId)) {
                finalAccess = true;
            } else if (location.state?.hasAccess === true) {
                
                finalAccess = true;
            }

            if (finalAccess) {
                setHasAccess(true);
            } else if (!location.state?.hasAccess) {
                setHasAccess(false);
            }

            setIsOverallLoading(false);
        } else {
            setIsOverallLoading(true);
        }
    }, [
        isUserDataLoaded, isScenarioDataLoaded, preview, scenario, 
        authUserId, fetchedUser, scenarioId, location.state?.hasAccess, setHasAccess
    ]);

    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentSituationId]);

    const clearScenarioKeys = useCallback(async () => {
        if (authUserId) {
            try {
                await axios.patch(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/users/${authUserId}/removeKeys`,
                    { scenarioId: scenario._id }, { withCredentials: true }
                );
                const userResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/users/${authUserId}`, { withCredentials: true }
                );
                setUserKeys(userResponse.data.keys || []);
            } catch (error) {
                console.error("Error removing/updating keys for logged-in user:", error);
            }
        } else {
            const storedKeyObjectsRaw = sessionStorage.getItem('userKeys');
            let storedKeyObjects = storedKeyObjectsRaw ? JSON.parse(storedKeyObjectsRaw) : [];
            const updatedKeyObjects = storedKeyObjects.filter(keyObj => keyObj.scenarioId !== scenario._id);
            sessionStorage.setItem('userKeys', JSON.stringify(updatedKeyObjects));
            setUserKeys(updatedKeyObjects.map(ko => ko.keyId));
        }
    }, [authUserId, scenario?._id, setUserKeys]);

    const handleRestart = useCallback(async () => {
        if (scenario?._id) {
            contextSetCurrentSituationId(scenario._id, null);
            await clearScenarioKeys();
        }
    }, [scenario, contextSetCurrentSituationId, clearScenarioKeys]);
    
    const handleExitPreview = useCallback(async () => {
        if (scenario?._id) {
            navigate(`/create/${scenarioId}/canvas`);
            contextSetCurrentSituationId(scenario._id, null);
            await clearScenarioKeys();
        }
    }, [scenario, scenarioId, navigate, contextSetCurrentSituationId, clearScenarioKeys]);
    
    const handleReturnToFeedback = useCallback(() => {
        if (fetchedUser) {
            navigate(`/user/${fetchedUser.username}`, { state: { activeTab: 'Feedback' } });
        }
        setFromFeedback(false);
    }, [fetchedUser, navigate]);

    const toggleToC = useCallback(() => {
        setIsToCVisible(prev => !prev);
    }, []);

    const setCurrentSituationIdWrapper = useCallback((situationId) => {
        if (scenario?._id) {
            contextSetCurrentSituationId(scenario._id, situationId);
        }
    }, [scenario?._id, contextSetCurrentSituationId]);


    if (isOverallLoading) {
        return <Loading />;
    }

    if (!hasAccess && scenario?.isPrivate && !preview) {
        return (
            <ScenarioPassword
                scenarioPassword={scenario.password}
                setHasAccess={setHasAccess}
                passwordError={passwordError}
                setPasswordError={setPasswordError}
            />
        );
    }

    if (!hasAccess || !scenario) { 
        return <div>Access Denied or Scenario not found.</div>;
    }

    return (
        <>
            {fromFeedback && !preview && (
                <button className={styles.previewButton} onClick={handleReturnToFeedback}>Back</button>
            )}

            {!currentSituationId ? (
                <ViewScenarioTitlePage
                    userId={authUserId}
                    incompleteLogs={fetchedUser?.incompleteLogs}
                    scenario={scenario}
                    setCurrentSituationId={setCurrentSituationIdWrapper}
                    preview={preview}
                    userKeys={userKeys}
                    setUserKeys={setUserKeys}
                />
            ) : (
                <div className={styles.container}>
                    {scenario.tableOfContents && (
                        <>
                            <div className={styles.header}>
                                <FaListOl 
                                    className={styles.tocButton} 
                                    onClick={toggleToC} 
                                    data-tooltip-id="viewscenario-tooltip" 
                                    data-tooltip-content="Table of Contents"
                                />
                                <div className={styles.tocTitle}>Table of Contents</div>
                            </div>
                            {isToCVisible && (
                                <ViewTableOfContents
                                    tableOfContents={scenario.tableOfContents}
                                    currentSituationId={currentSituationId}
                                    setCurrentSituationId={setCurrentSituationIdWrapper}
                                    toggleToC={toggleToC}
                                />
                            )}
                        </>
                    )}
                    <ViewSituation
                        userId={authUserId}
                        scenario={scenario}
                        canvas={canvas}
                        currentSituationId={currentSituationId}
                        setCurrentSituationId={setCurrentSituationIdWrapper}
                        preview={preview}
                        userLoading={userLoading}
                        userKeys={userKeys}
                        setUserKeys={setUserKeys}
                    />
                    <FaRedo 
                        className={styles.restartButton} 
                        onClick={handleRestart} 
                        data-tooltip-id="viewscenario-tooltip" 
                        data-tooltip-content="Restart Scenario"
                    />
                    {preview && (
                         <button 
                            type="button" 
                            aria-label="Exit Preview" 
                            className={styles.exitPreview} 
                            onClick={handleExitPreview} 
                            data-tooltip-id="viewscenario-tooltip" 
                            data-tooltip-content="Exit Preview"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>
            )}
            <Tooltip id="viewscenario-tooltip" place="bottom" style={{ zIndex: 9999 }} />
        </>
    );
};

export default ViewScenario;